import { useEffect, useRef } from 'react'
import { useAuthStore } from '../contexts/authStore'
import { authAPI } from '../services/api'

/**
 * Prueft/haelt die Anmeldung aktuell — entkoppelt in zwei Mechanismen, nach
 * TireVault-Vorbild (apps/tirevault/frontend/src/hooks/useAuthQueries.ts,
 * siehe Memory project_rentalhub_superadmin_portal_migration fuer den Kontext
 * der Session-System-Angleichung):
 *
 * 1. Der autoritative Check (Mount + Tab-Fokus/visibilitychange) laeuft IMMER,
 *    unabhaengig vom Idle-Status — das ist die einzige Quelle, die wirklich
 *    entscheidet, ob der Nutzer noch angemeldet ist.
 * 2. Der Heartbeat (Intervall, idle-gegatet) ruft denselben Check periodisch
 *    auf, nur um die Sliding-Session bei echter Nutzung serverseitig zu
 *    verlaengern (jeder authentifizierte Request an /auth/me verlaengert die
 *    DB-Session, siehe core/dependencies.py::get_current_user).
 *
 * Vorher gab es hier nur einen einzigen Mount-Check ohne Heartbeat und ohne
 * Fokus-Recheck — eine abgelaufene Sitzung wurde erst nach einem manuellen
 * Reload erkannt. Portal nutzt (anders als die anderen Apps) kein
 * react-query, daher hier ein schlankes, abhaengigkeitsfreies Aequivalent.
 */
const IDLE_THRESHOLD_MS = 5 * 60 * 1000
const HEARTBEAT_INTERVAL_MS = 4 * 60 * 1000
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const

export const useAuthCheck = () => {
  const setAuth = useAuthStore((state) => state.setAuth)
  const clearAuth = useAuthStore((state) => state.clearAuth)
  const lastActivityRef = useRef(Date.now())

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authAPI.me()
        const { email, user_type } = response.data
        setAuth(email, user_type)
      } catch {
        clearAuth()
      }
    }

    // Autoritativer Check: sofort beim Mount, und immer bei Tab-Rueckkehr —
    // nie vom Idle-Status abhaengig.
    checkAuth()
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkAuth()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Heartbeat: nur bei sichtbarem UND genutztem Tab, verlaengert die
    // Sliding-Session ueber denselben /auth/me-Aufruf.
    const markActive = () => { lastActivityRef.current = Date.now() }
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, markActive, { passive: true }))
    const isIdle = () => Date.now() - lastActivityRef.current > IDLE_THRESHOLD_MS
    const heartbeat = () => {
      if (document.visibilityState === 'visible' && !isIdle()) checkAuth()
    }
    const interval = window.setInterval(heartbeat, HEARTBEAT_INTERVAL_MS)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActive))
      window.clearInterval(interval)
    }
  }, [setAuth, clearAuth])
}
